import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { auth } from '../firebase';
import { getAdditionalUserInfo, getIdToken, getRedirectResult, onAuthStateChanged, reload } from 'firebase/auth';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  clearPendingBackendLoginSync,
  clearPendingSignupMetadata,
  consumePendingBackendLoginSync,
  readPendingSignupMetadata,
} from '../utils/auth';
import { trackAnalyticsEvent } from '../utils/analyticsServices';

const IS_E2E = process.env.REACT_APP_E2E === 'true';
const SESSION_SNAPSHOT_PREFIX = 'auth:sessionSnapshot:';
const SESSION_SNAPSHOT_VERSION = 1;
const SESSION_SNAPSHOT_MAX_AGE_MS = 10 * 60 * 1000;
const USER_NOT_FOUND_CODE = 'USER_NOT_FOUND';

const AuthContext = createContext({
  user: null,
  loading: true,
  profile: null,
  profileLoading: false,
  isPremium: false,
  subscriptionEndsAt: null,
  isAdmin: false,
  forceRefreshToken: async () => {},
  updateProfileLanguage: () => {},
});

const normalizeServerProfile = (data) => {
  if (!data || typeof data !== 'object') return null;

  return {
    sourceLang: typeof data.source_lang === 'string' ? data.source_lang.trim().toLowerCase() : null,
    targetLang: typeof data.target_lang === 'string' ? data.target_lang.trim().toLowerCase() : null,
    hasSelectedLanguage: Boolean(data.hasSelectedLanguage),
    hasCustomLists: Boolean(data.hasCustomLists),
    hasCustomVocab: Boolean(data.hasCustomVocab),
    hasMockExams: Boolean(data.hasMockExams),
    isPremium: Boolean(data.isPremium),
    subscriptionEndsAt: data.subscriptionEndsAt ?? null,
    isAdmin: Boolean(data.isAdmin),
  };
};

const isCanonicalProfile = (profile) => {
  if (!profile || typeof profile !== 'object') return false;

  const isNullableString = (value) => value === null || typeof value === 'string';
  return (
    isNullableString(profile.sourceLang) &&
    isNullableString(profile.targetLang) &&
    typeof profile.hasSelectedLanguage === 'boolean' &&
    typeof profile.hasCustomLists === 'boolean' &&
    typeof profile.hasCustomVocab === 'boolean' &&
    typeof profile.hasMockExams === 'boolean' &&
    typeof profile.isPremium === 'boolean' &&
    isNullableString(profile.subscriptionEndsAt) &&
    typeof profile.isAdmin === 'boolean'
  );
};

const snapshotKey = (uid) => `${SESSION_SNAPSHOT_PREFIX}${uid}`;

const readSessionSnapshot = (uid) => {
  if (!uid || typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(snapshotKey(uid));
    if (!raw) return null;
    const snapshot = JSON.parse(raw);
    const isFresh =
      snapshot?.version === SESSION_SNAPSHOT_VERSION &&
      snapshot?.uid === uid &&
      typeof snapshot.checkedAt === 'number' &&
      Date.now() - snapshot.checkedAt >= 0 &&
      Date.now() - snapshot.checkedAt <= SESSION_SNAPSHOT_MAX_AGE_MS;
    if (isFresh && isCanonicalProfile(snapshot?.profile)) return snapshot.profile;
    window.localStorage.removeItem(snapshotKey(uid));
  } catch (_) {
    try {
      window.localStorage.removeItem(snapshotKey(uid));
    } catch (_) {
      // Storage may be unavailable.
    }
  }

  return null;
};

const persistSessionSnapshot = (uid, profile) => {
  if (!uid || !profile || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      snapshotKey(uid),
      JSON.stringify({ version: SESSION_SNAPSHOT_VERSION, uid, checkedAt: Date.now(), profile }),
    );
  } catch (_) {
    // A missing or full localStorage must not affect auth.
  }
};

const clearSessionSnapshot = (uid) => {
  if (!uid || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(snapshotKey(uid));
  } catch (_) {
    // Storage may be unavailable.
  }
};

// Used only for explicit logins, first-time provisioning, and staged signup metadata.
export const syncUserWithBackend = async (user, { forceRefresh = false, throwOnError = false } = {}) => {
  if (!user) return null;

  try {
    const pendingSignupMetadata = readPendingSignupMetadata();
    const token = await getIdToken(user, forceRefresh);
    const response = await axios.post(`${API_BASE_URL}/user/login`, pendingSignupMetadata ?? {}, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (pendingSignupMetadata) clearPendingSignupMetadata();
    return normalizeServerProfile(response?.data?.user);
  } catch (error) {
    if (throwOnError) throw error;
    console.error('Error syncing user with backend:', error);
    return null;
  }
};

const fetchUserSession = async (user) => {
  const token = await user.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/user/session`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return normalizeServerProfile(response.data);
};

const fetchLegacyUserProfile = async (user) => {
  const token = await user.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/user/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return normalizeServerProfile(response.data);
};

const isMissingUserError = (error) =>
  error?.response?.status === 404 && error?.response?.data?.code === USER_NOT_FOUND_CODE;

const isLegacySessionRouteError = (error) => error?.response?.status === 404 && !isMissingUserError(error);

const markConfirmedMissing = (error) => {
  if (error && typeof error === 'object') error.confirmedAccountMissing = true;
  return error;
};

const shouldClearSessionForError = (error) =>
  [401, 403, 410].includes(error?.response?.status) ||
  isMissingUserError(error) ||
  error?.confirmedAccountMissing === true;

const provisionUser = async (user, missingError) => {
  const provisioned = await syncUserWithBackend(user);
  if (!provisioned) throw markConfirmedMissing(missingError);
  return provisioned;
};

const fetchCompatibleUserSession = async (user, { allowProvisioning }) => {
  try {
    return { profile: await fetchUserSession(user), provisioned: false };
  } catch (sessionError) {
    if (isMissingUserError(sessionError)) {
      if (!allowProvisioning) throw sessionError;
      await provisionUser(user, sessionError);
      return { profile: await fetchUserSession(user), provisioned: true };
    }

    if (!isLegacySessionRouteError(sessionError)) throw sessionError;

    try {
      return { profile: await fetchLegacyUserProfile(user), provisioned: false };
    } catch (legacyError) {
      if (legacyError?.response?.status !== 404 || !allowProvisioning) throw legacyError;
      await provisionUser(user, legacyError);
      try {
        return { profile: await fetchLegacyUserProfile(user), provisioned: true };
      } catch (retryError) {
        if (retryError?.response?.status === 404) markConfirmedMissing(retryError);
        throw retryError;
      }
    }
  }
};

const clearPremiumQueries = (queryClient) => {
  queryClient.removeQueries({
    predicate: (query) =>
      [
        'topics',
        'chapters',
        'fide-vocab-topic-access',
        'default-vocab',
        'practice-section',
        'practice-map-topic-completion',
      ].includes(query.queryKey[0]),
  });
};

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const activeUidRef = useRef(null);
  const profileRef = useRef(null);
  const profilePatchRef = useRef(null);
  const profilePatchVersionRef = useRef(0);

  useEffect(() => {
    if (IS_E2E) return undefined;

    let cancelled = false;
    void getRedirectResult(auth)
      .then((result) => {
        if (!cancelled && result && getAdditionalUserInfo(result)?.isNewUser) {
          trackAnalyticsEvent('sign_up', { method: 'google' });
        }
      })
      .catch(() => {
        // A redirect-result lookup must never interfere with restoring auth state.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const applyProfile = useCallback((uid, nextProfile) => {
    profileRef.current = nextProfile;
    setProfile(nextProfile);
    persistSessionSnapshot(uid, nextProfile);
  }, []);

  const updateProfileLanguage = useCallback(
    (targetLang) => {
      const normalizedTargetLang = typeof targetLang === 'string' ? targetLang.trim().toLowerCase() : null;
      const uid = activeUidRef.current;
      if (!uid || !['fr', 'de'].includes(normalizedTargetLang) || !isCanonicalProfile(profileRef.current)) {
        return null;
      }

      const nextProfile = {
        ...profileRef.current,
        targetLang: normalizedTargetLang,
        hasSelectedLanguage: true,
      };
      profilePatchVersionRef.current += 1;
      profilePatchRef.current = {
        uid,
        version: profilePatchVersionRef.current,
        profile: {
          targetLang: nextProfile.targetLang,
          hasSelectedLanguage: nextProfile.hasSelectedLanguage,
        },
      };
      applyProfile(uid, nextProfile);
      return nextProfile;
    },
    [applyProfile],
  );

  const verifySession = useCallback(
    async (nextUser, { allowProvisioning = true } = {}) => {
      const profilePatchVersionAtStart = profilePatchVersionRef.current;
      try {
        let backendLoginCompleted = false;
        if (readPendingSignupMetadata()) {
          const provisionedProfile = await syncUserWithBackend(nextUser, { throwOnError: true });
          if (!provisionedProfile) throw new Error('Failed to provision account');
          backendLoginCompleted = true;
          consumePendingBackendLoginSync();
        } else if (consumePendingBackendLoginSync()) {
          backendLoginCompleted = Boolean(await syncUserWithBackend(nextUser));
        }

        const session = await fetchCompatibleUserSession(nextUser, {
          allowProvisioning: allowProvisioning && !backendLoginCompleted,
        });
        const sessionProfile = session.profile;

        if (activeUidRef.current !== nextUser.uid) return null;
        const latestPatch = profilePatchRef.current;
        const nextProfile =
          latestPatch?.uid === nextUser.uid && latestPatch.version > profilePatchVersionAtStart
            ? { ...sessionProfile, ...latestPatch.profile }
            : sessionProfile;
        if (profileRef.current && profileRef.current.isPremium !== nextProfile?.isPremium)
          clearPremiumQueries(queryClient);
        applyProfile(nextUser.uid, nextProfile);
        return nextProfile;
      } catch (error) {
        if (activeUidRef.current === nextUser.uid && shouldClearSessionForError(error)) {
          clearSessionSnapshot(nextUser.uid);
          clearPremiumQueries(queryClient);
          profileRef.current = null;
          setProfile(null);
        }
        console.error('Failed to verify account session:', error);
        return null;
      } finally {
        if (activeUidRef.current === nextUser.uid) setProfileLoading(false);
      }
    },
    [applyProfile, queryClient],
  );

  useEffect(() => {
    if (IS_E2E) {
      // Tests can steer identity/role by setting localStorage["e2e:profileOverrides"]
      // (via page.addInitScript) before the app mounts, e.g. { uid: 'admin', isAdmin: true }.
      let overrides = {};
      try {
        overrides = JSON.parse(window.localStorage.getItem('e2e:profileOverrides') || '{}') || {};
      } catch (_) {
        overrides = {};
      }

      if (overrides.guest) {
        activeUidRef.current = null;
        setUser(null);
        setLoading(false);
        setProfile(null);
        setProfileLoading(false);
        return () => {};
      }

      const mockUser = {
        uid: overrides.uid || 'e2e-user',
        emailVerified: true,
        getIdToken: async () => overrides.token || 'e2e-token',
      };
      activeUidRef.current = mockUser.uid;
      setUser(mockUser);
      setLoading(false);
      setProfile({
        sourceLang: 'fr',
        targetLang: 'en',
        hasSelectedLanguage: true,
        hasCustomLists: false,
        hasCustomVocab: false,
        hasMockExams: false,
        isPremium: Boolean(overrides.isPremium),
        subscriptionEndsAt: null,
        isAdmin: Boolean(overrides.isAdmin),
      });
      setProfileLoading(false);
      return () => {};
    }

    let cancelled = false;
    const unsub = onAuthStateChanged(auth, (nextUser) => {
      if (cancelled) return;

      const previousUid = activeUidRef.current;
      activeUidRef.current = nextUser?.uid ?? null;
      setUser(nextUser);
      setLoading(false);

      if (!nextUser) {
        clearPendingBackendLoginSync();
        if (previousUid) clearSessionSnapshot(previousUid);
        clearPremiumQueries(queryClient);
        profilePatchRef.current = null;
        profilePatchVersionRef.current = 0;
        profileRef.current = null;
        setProfile(null);
        setProfileLoading(false);
        return;
      }

      if (previousUid && previousUid !== nextUser.uid) {
        clearSessionSnapshot(previousUid);
        clearPremiumQueries(queryClient);
        profilePatchRef.current = null;
        profilePatchVersionRef.current = 0;
      }

      const snapshot = readSessionSnapshot(nextUser.uid);
      profileRef.current = snapshot;
      setProfile(snapshot);
      setProfileLoading(!snapshot);
      void verifySession(nextUser);
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [queryClient, verifySession]);

  const forceRefreshToken = useCallback(async () => {
    if (IS_E2E) return profile;
    if (!auth.currentUser) return null;

    setProfileLoading(true);
    try {
      await reload(auth.currentUser);
      await auth.currentUser.getIdToken(true);
      setUser(auth.currentUser);
      return await verifySession(auth.currentUser, { allowProvisioning: true });
    } finally {
      if (activeUidRef.current === auth.currentUser?.uid) setProfileLoading(false);
    }
  }, [profile, verifySession]);

  const isPremium = profile?.isPremium ?? false;
  const subscriptionEndsAt = profile?.subscriptionEndsAt ?? null;
  const isAdmin = profile?.isAdmin ?? false;
  const value = useMemo(
    () => ({
      user,
      loading,
      profile,
      profileLoading,
      isPremium,
      subscriptionEndsAt,
      isAdmin,
      forceRefreshToken,
      updateProfileLanguage,
    }),
    [
      user,
      loading,
      profile,
      profileLoading,
      isPremium,
      subscriptionEndsAt,
      isAdmin,
      forceRefreshToken,
      updateProfileLanguage,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
